import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import {
  BodyText,
  Button,
  checkIfRecaptchaEnabled,
  Form,
  TEST_GRECAPTCHA,
  TextLink,
  theme,
  useNoticeBannersStackContext,
  useReCaptcha,
} from '@sweetgreen/garnish';

import { useAuthFlow } from '@order/AuthFlow';
import { useIsLoggedIn } from '@order/AuthMachine';
import { PageWrapper } from '@order/components';
import { useCustomer } from '@order/Customer';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry, useTrackEffect } from '@order/Telemetry';

import type { LoginScreenProps } from '../../navigation/AppNavigation.props';
import type { LoginFormData } from './hooks';
import {
  useLogIn,
  useLoginForm,
  useShouldUseDecoupledCustomerLogin,
} from './hooks';

// ────────────────────────────────────────────────────────────────────────────────

export const LoginScreen = ({ navigation, route }: LoginScreenProps) => {
  const { redirect: redirectParam } = route.params ?? {};
  const { customer, isFetchingCustomer, signIn: handleSignIn } = useCustomer();
  const isLoggedIn = useIsLoggedIn();
  const { redirectAfterSignIn } = useAuthFlow();
  const { track } = useTelemetry();

  // ─────────────────────────────────────────────────────────────────

  const handlePressJoin = useCallback(() => {
    navigation.navigate('Join', { redirect: redirectParam });
  }, [navigation, redirectParam]);

  const handlePressForgotPassword = useCallback(() => {
    navigation.navigate('ForgotPassword', { redirect: redirectParam });
  }, [navigation, redirectParam]);

  // ─── TELEMETRY ───────────────────────────────────────────────────

  useTrackEffect('sign-in.view');

  // ─────────────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    if (isFetchingCustomer) return;

    if (isLoggedIn && customer?.id) {
      redirectAfterSignIn(redirectParam);
      track('sign-in.success', { userId: customer?.id });
    }
  }, [
    customer?.id,
    isFetchingCustomer,
    isLoggedIn,
    redirectParam,
    track,
    redirectAfterSignIn,
  ]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <LoginForm
      testID="login-form"
      signIn={handleSignIn}
      onPressJoin={handlePressJoin}
      onPressForgotPassword={handlePressForgotPassword}
    />
  );
};

export const LoginForm = (props: LoginFormProps) => {
  const { testID, signIn, onPressJoin, onPressForgotPassword } = props;

  const { isSigningIn, isFetchingCustomer, isStaleCustomer } = useCustomer();

  // ────────────────────────────────────────────────────────────────────

  const { form, showVerificationErrors } = useLoginForm();
  const { control, handleSubmit, formState } = form;
  const { errors, isSubmitting } = formState;

  // ─── OPS-customer-decoupling-login-enabled ───────────────────────────

  const shouldUseDecoupledCustomerLogin = useShouldUseDecoupledCustomerLogin();
  const { logIn, isLoggingIn } = useLogIn({
    onValidationError: showVerificationErrors,
  });

  const logInCustomer = shouldUseDecoupledCustomerLogin ? logIn : signIn;
  const isCustomerLoggingIn = shouldUseDecoupledCustomerLogin
    ? isLoggingIn
    : isSigningIn;

  // ─────────────────────────────────────────────────────────────────────

  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  // ─── ReCaptcha ───────────────────────────────────────────────────

  const { reCaptchaMachine, reCaptchaRef } = useReCaptcha();

  const [reCaptchaState] = reCaptchaMachine;
  const isLoadingRecaptcha = reCaptchaState.matches('loading');
  const isExecutingRecaptcha = reCaptchaState.matches('executing');

  // ─── Helpers ────────────────────────────────────────────────────────

  const signInUser = useCallback(
    async (formData: LoginFormData) => {
      const { email, password, grecaptcha = TEST_GRECAPTCHA } = formData;

      await logInCustomer({ email, password, grecaptcha });

      // reset recaptcha after request
      reCaptchaRef.current?.resetRecaptcha();
    },
    [reCaptchaRef, logInCustomer],
  );

  const onRecaptchaVerify = useCallback(
    async (grecaptcha: string) => {
      if (!grecaptcha) return;

      const formValues = form.getValues();

      await signInUser({ ...formValues, grecaptcha });
    },
    [form, signInUser],
  );

  const onRecaptchaError = useCallback(() => {
    addNoticeBanner({ text: t('general.error'), palette: 'caution' });
  }, [addNoticeBanner, t]);

  const submitForm = useMemo(() => {
    const isRecaptchaEnabled = checkIfRecaptchaEnabled();

    return handleSubmit(async (formData) => {
      await (isRecaptchaEnabled
        ? reCaptchaRef.current?.executeRecaptcha()
        : signInUser(formData));
    });
  }, [handleSubmit, reCaptchaRef, signInUser]);

  // ────────────────────────────────────────────────────────────────────

  const shouldShowLoadingSubmitBtn =
    isSubmitting ||
    isCustomerLoggingIn ||
    isExecutingRecaptcha ||
    isFetchingCustomer ||
    isStaleCustomer;

  // ─────────────────────────────────────────────────────────────────

  return (
    <PageWrapper>
      <Form.Container
        testID={testID}
        reCaptchaRef={reCaptchaRef}
        reCaptchaMachine={reCaptchaMachine}
        onRecaptchaVerify={onRecaptchaVerify}
        onRecaptchaError={onRecaptchaError}
      >
        <Form.Title>{t('login.title')}</Form.Title>

        <Form.Subtitle>
          <BodyText size={3}>{t('login.subtitle')} </BodyText>

          <View style={styles.joinBtnWrapper}>
            <Button
              onPress={onPressJoin}
              palette="secondary"
              testID="button-join-now"
            >
              {t('login.join-button')}
            </Button>
          </View>
        </Form.Subtitle>

        {/* ========== email ========== */}

        <Form.Group>
          <Form.TextField
            name="email"
            keyboardType="email-address"
            control={control}
            rules={{ required: true }}
            testID="field-email"
            label={t('form.email')}
            hideSubmitButton
            onSubmit={submitForm}
            autoComplete="email"
            notice={errors.email?.message}
          />
        </Form.Group>

        {/* ========== password ========== */}

        <Form.Group>
          <Form.TextFieldPassword
            name="password"
            control={control}
            rules={{ required: true }}
            testID="field-password"
            label={t('form.password')}
            hideSubmitButton
            onSubmit={submitForm}
            autoComplete="password"
            notice={errors.password?.message}
            toggleTestID="field-password-toggle"
          />
        </Form.Group>

        {/* ========== ========== ========== */}

        <Form.SubmitButton
          testID="button-sign-in"
          onPress={submitForm}
          isLoading={shouldShowLoadingSubmitBtn}
          disabled={isLoadingRecaptcha}
        >
          {t('login.sign-in-button')}
        </Form.SubmitButton>

        {/* ========== ========== ========== */}

        <View style={styles.forgotPasswordWrapper}>
          <TextLink
            onPress={onPressForgotPassword}
            size={4}
            testID="link-forgot-password"
          >
            {t('login.forgot-password-button')}
          </TextLink>
        </View>
      </Form.Container>
    </PageWrapper>
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type LoginFormProps = {
  readonly signIn: ReturnType<typeof useCustomer>['signIn'];
  readonly onPressJoin: () => void;
  readonly onPressForgotPassword: () => void;
} & Pick<ViewProps, 'testID'>;

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  joinBtnWrapper: {
    alignItems: 'flex-start',
    paddingTop: theme.spacing.medium,
  },
  forgotPasswordWrapper: {
    alignItems: 'flex-start',
    paddingTop: theme.spacing.xlarge,
  },
});
